import React, { useCallback, useEffect, useState } from 'react';
import Modal from '../../components/modal';
import { useInternals } from '../../context/internalContext';
import { useAuxiliaries } from '../../context/auxiliariesContext';
import { useUsers } from '../../context/usersContext';
import QuickAddModal from '../../components/modal/quickAddModal';
import TooltipInfo from '../../components/TooltipInfo';
import Lupa from '../../assets/icons/lupa.svg';
import useSubmitLock from '../../utils/useSubmitLock';
import './discipline.css'; // Arquivo exclusivo para disciplina

// Nome busca com 3+ letras; CPF só com os 11 dígitos.
const getInternalSearchParams = (value) => {
  const rawValue = String(value || '').trim();
  const isCpfSearch = /^[0-9.\-\s/]+$/.test(rawValue);

  if (isCpfSearch) {
    const digitsOnly = rawValue.replace(/\D/g, '');
    return {
      isValid: digitsOnly.length === 11,
      params: { cpf: digitsOnly },
    };
  }

  return {
    isValid: rawValue.length >= 3,
    params: { name: rawValue },
  };
};

const DisciplineNotes = ({ internalId, onClose, onUpdate }) => {
  const { createDisciplineNote, listInternals } = useInternals();
  const { buildUnitReadParams } = useUsers();
  const {
    listFunctions,
    listParticularSigns,
    createFunctionInUnit,
    createParticularSign,
  } = useAuxiliaries();

  const [formData, setFormData] = useState({
    partners_crime: [],
    enemies: [],
    function: '',
    notes: '',
    date: '',
  });

  const [signOptions, setSignOptions] = useState([]);
  const [selectedSigns, setSelectedSigns] = useState([]);

  const [functions, setFunctions] = useState([]);

  // Estados separados para cada campo de busca
  const [partnersSearchQuery, setPartnersSearchQuery] = useState('');
  const [enemiesSearchQuery, setEnemiesSearchQuery] = useState('');
  const [partnersResults, setPartnersResults] = useState([]);
  const [enemiesResults, setEnemiesResults] = useState([]);
  const { isSubmitting, runWithSubmitLock } = useSubmitLock();

  const normalizeUpperText = (value) =>
    String(value || '')
      .trim()
      .replace(/\s+/g, ' ')
      .toUpperCase();

  const fetchFunctions = useCallback(async () => {
    try {
      const response = await listFunctions();
      const functionList = response?.results || [];
      setFunctions(functionList);
      return functionList;
    } catch (error) {
      console.error('Erro ao buscar funções:', error);
      return [];
    }
  }, [listFunctions]);

  const fetchSigns = useCallback(async () => {
    const result = await listParticularSigns();
    const signsList = result?.results || [];
    setSignOptions(signsList);
    return signsList;
  }, [listParticularSigns]);

  useEffect(() => {
    fetchFunctions();
  }, [fetchFunctions]);

  useEffect(() => {
    fetchSigns();
  }, [fetchSigns]);

  // Busca internos de forma separada para cada campo, com debounce: só consulta a API quando o
  // usuário para de digitar. Devolve a limpeza do efeito (cancela a espera e ignora resposta atrasada).
  const scheduleInternalsSearch = useCallback((query, setResults) => {
    const searchConfig = getInternalSearchParams(query);
    if (!searchConfig.isValid) {
      setResults([]);
      return undefined;
    }

    let isCurrent = true;
    const timer = setTimeout(async () => {
      try {
        const response = await listInternals(buildUnitReadParams(searchConfig.params));
        if (isCurrent) setResults(response?.results || []);
      } catch (error) {
        console.error('Erro ao buscar internos:', error);
      }
    }, 400);

    return () => {
      isCurrent = false;
      clearTimeout(timer);
    };
  }, [listInternals, buildUnitReadParams]);

  useEffect(
    () => scheduleInternalsSearch(partnersSearchQuery, setPartnersResults),
    [partnersSearchQuery, scheduleInternalsSearch]
  );

  useEffect(
    () => scheduleInternalsSearch(enemiesSearchQuery, setEnemiesResults),
    [enemiesSearchQuery, scheduleInternalsSearch]
  );

  // Seleciona e remove internos de cada lista
  const handleSelectInternal = (internal, field) => {
    setFormData((prevData) => {
      const updatedList = prevData[field].some((item) => item.id === internal.id)
        ? prevData[field].filter((item) => item.id !== internal.id) // Remove se já estiver selecionado
        : [...prevData[field], internal]; // Adiciona se ainda não estiver

      return { ...prevData, [field]: updatedList };
    });

    // Reseta apenas o campo de busca correspondente
    if (field === 'partners_crime') {
      setPartnersResults([]);
      setPartnersSearchQuery('');
    } else if (field === 'enemies') {
      setEnemiesResults([]);
      setEnemiesSearchQuery('');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    await runWithSubmitLock(async () => {
      try {
        const dataToSend = {
          ...formData,
          partners_crime: formData.partners_crime.map((i) => i.id),
          enemies: formData.enemies.map((i) => i.id),
          internal: internalId,
          signs: selectedSigns,
        };
        await createDisciplineNote(dataToSend);

        if (onUpdate) {
          onUpdate(); // Atualiza a linha do tempo
        }

        onClose();
      } catch (error) {
        console.error('Erro ao registrar nota interna:', error);
      }
    });
  };

  const handleAddParticularSign = async (description) => {
    const normalizedDescription = normalizeUpperText(description);
    if (!normalizedDescription) return;

    const created = await createParticularSign({ description: normalizedDescription });
    const refreshedSigns = await fetchSigns();

    let signId = created?.id;
    if (!signId) {
      const createdByName = refreshedSigns.find(
        (sign) => normalizeUpperText(sign?.description) === normalizedDescription
      );
      signId = createdByName?.id;
    }

    const parsedSignId = Number(signId);
    if (Number.isFinite(parsedSignId) && parsedSignId > 0 && !selectedSigns.includes(parsedSignId)) {
      setSelectedSigns((prev) => [...prev, parsedSignId]);
    }
  };

  const handleAddFunctionInUnit = async (name) => {
    const normalizedName = normalizeUpperText(name);
    if (!normalizedName) return;

    const created = await createFunctionInUnit({ name: normalizedName });
    const refreshedFunctions = await fetchFunctions();

    let functionId = created?.id;
    if (!functionId) {
      const createdByName = refreshedFunctions.find(
        (func) => normalizeUpperText(func?.name) === normalizedName
      );
      functionId = createdByName?.id;
    }

    if (functionId) {
      setFormData((prev) => ({ ...prev, function: String(functionId) }));
    }
  };

  return (
    <Modal title="Registrar Nota Disciplinar" isOpen={true} closeModal={onClose}>
      <form onSubmit={handleSubmit} className="discipline-form">
        {/* Buscar Parceiros de Crime */}
        <div className="discipline-search-container">
          <label>
            Parceiro na ação <TooltipInfo text="Digite nome ou CPF para buscar o interno." />
          </label>
          <div className="discipline-search-wrapper">
            <input
              type="text"
              placeholder="Digite nome ou CPF"
              value={partnersSearchQuery}
              onChange={(e) => setPartnersSearchQuery(e.target.value)}
            />
            <img src={Lupa} alt="Buscar" className="discipline-search-icon" />
          </div>
          {partnersResults.length > 0 && (
            <div className="discipline-search-results">
              {partnersResults.map((internal) => (
                <div key={internal.id} onClick={() => handleSelectInternal(internal, 'partners_crime')} className="discipline-search-result-item">
                  {internal.name} - {internal.cpf}
                </div>
              ))}
            </div>
          )}
          <div className="discipline-selected">
            {formData.partners_crime.map((internal) => (
              <span key={internal.id} onClick={() => handleSelectInternal(internal, 'partners_crime')} className="discipline-selected-item">
                {internal.name} ✕
              </span>
            ))}
          </div>
        </div>

        {/* Buscar Inimigos */}
        <div className="discipline-search-container">
          <label>
            Inimigos <TooltipInfo text="Digite nome ou CPF para buscar o interno." />
          </label>
          <div className="discipline-search-wrapper">
            <input
              type="text"
              placeholder="Digite nome ou CPF"
              value={enemiesSearchQuery}
              onChange={(e) => setEnemiesSearchQuery(e.target.value)}
            />
            <img src={Lupa} alt="Buscar" className="discipline-search-icon" />
          </div>
          {enemiesResults.length > 0 && (
            <div className="discipline-search-results">
              {enemiesResults.map((internal) => (
                <div key={internal.id} onClick={() => handleSelectInternal(internal, 'enemies')} className="discipline-search-result-item">
                  {internal.name} - {internal.cpf}
                </div>
              ))}
            </div>
          )}
          <div className="discipline-selected">
            {formData.enemies.map((internal) => (
              <span key={internal.id} onClick={() => handleSelectInternal(internal, 'enemies')} className="discipline-selected-item">
                {internal.name} ✕
              </span>
            ))}
          </div>
        </div>

        {/* Sinais Particulares */}
        <label>Sinais Particulares:</label>
        <div className="container-quick-modal">
          <div className="form-group-lager discipline-signs-group">
            <select
              value=""
              onChange={(e) => {
                const selectedId = parseInt(e.target.value, 10);
                if (selectedId && !selectedSigns.includes(selectedId)) {
                  setSelectedSigns([...selectedSigns, selectedId]);
                }
              }}
            >
              <option value="">Selecione um sinal</option>
              {signOptions.map(sign => (
                <option key={sign.id} value={sign.id}>{sign.description}</option>
              ))}
            </select>
          </div>
          <QuickAddModal
            label="Sinal Particular"
            fields={[
              { name: 'description', placeholder: 'Descrição do sinal', type: 'text' },
            ]}
            onAdd={async (data) => handleAddParticularSign(data.description)}
          />
        </div>
        <div className="discipline-selected">
          {selectedSigns.map((id) => {
            const sign = signOptions.find(s => s.id === id);
            return (
              <span key={id} onClick={() => setSelectedSigns(selectedSigns.filter(s => s !== id))} className="discipline-selected-item">
                {sign?.description} ✕
              </span>
            );
          })}
        </div>

        {/* Função na Unidade */}
        <label>Função na Unidade:</label>
        <div className="container-quick-modal">
          <div className="form-group-lager">
            <select
              value={formData.function}
              onChange={(e) => setFormData({ ...formData, function: e.target.value })}
              required
            >
              <option value="">Selecione uma função</option>
              {functions.map((func) => (
                <option key={func.id} value={func.id}>
                  {func.name}
                </option>
              ))}
            </select>
          </div>
          <QuickAddModal
            label="Função da Unidade"
            fields={[
              { name: 'name', placeholder: 'Nome da função', type: 'text' },
            ]}
            onAdd={async (data) => handleAddFunctionInUnit(data.name)}
          />
        </div>

        {/* Campo de Notas */}
        <label>Descrição:</label>
        <textarea
          name="notes"
          value={formData.notes}
          onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
          required
        />

        {/* Data */}
        <label>Data:</label>
        <input
          type="datetime-local"
          name="date"
          value={formData.date}
          onChange={(e) => setFormData({ ...formData, date: e.target.value })}
          required
        />

        <button type="submit" disabled={isSubmitting}>
          {isSubmitting ? 'Salvando...' : 'Salvar'}
        </button>
      </form>
    </Modal>
  );
};

export default DisciplineNotes;
